import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface VideoRingUser {
  id: string;
  name: string;
  initials: string;
  status: 'ringing' | 'rejected' | 'accepted' | 'missed';
  rejectedAt?: string;
}

export interface VideoRingLevel {
  level: number;
  users: VideoRingUser[];
}

export interface VideoRingDetails {
  id: string;
  callId: string;
  requestedBy: {
    id: string;
    name: string;
    initials: string;
  } | null;
  currentLevel: number;
  status: string;
  levels: VideoRingLevel[];
  acceptedBy: VideoRingUser | null;
  createdAt: string;
  endedAt: string | null;
}

async function resolveUserInfo(userId: string, usersMap: Map<string, any>): Promise<{ name: string; initials: string }> {
  if (usersMap.has(userId)) {
    const user = usersMap.get(userId);
    const name = user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Usuário';
    const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
    return { name, initials };
  }
  return { name: 'Usuário', initials: 'U' };
}

export function useVideoRingDetails(callId: string | undefined | null) {
  return useQuery({
    queryKey: ['video-ring-details', callId],
    queryFn: async (): Promise<VideoRingDetails | null> => {
      if (!callId) return null;

      // Fetch ring request for this call
      const { data: ringRequest, error } = await supabase
        .from('video_call_ring_requests' as any)
        .select('*')
        .eq('call_id', callId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !ringRequest) {
        console.log('[useVideoRingDetails] No ring request found for call:', callId);
        return null;
      }

      // Collect all user IDs we need to resolve
      const userIds = new Set<string>();
      const metadata = (ringRequest as any).metadata || {};

      if ((ringRequest as any).requested_by) userIds.add((ringRequest as any).requested_by);
      if ((ringRequest as any).accepted_by) userIds.add((ringRequest as any).accepted_by);
      
      // Add users from each level
      for (let level = 1; level <= 3; level++) {
        const levelUsers = metadata[`level_${level}_users`] || [];
        levelUsers.forEach((id: string) => userIds.add(id));
      }

      // Add users from rejections
      const rejections = metadata.rejections || [];
      rejections.forEach((r: any) => {
        if (r.user_id) userIds.add(r.user_id);
      });

      // Fetch all users at once
      const { data: users } = await supabase
        .from('app_users')
        .select('id, first_name, last_name, display_name')
        .in('id', Array.from(userIds));

      const usersMap = new Map(users?.map(u => [u.id, u]) || []);

      // Build levels data
      const levels: VideoRingLevel[] = [];
      for (let level = 1; level <= 3; level++) {
        const levelUserIds = metadata[`level_${level}_users`] || [];
        if (levelUserIds.length === 0) continue;

        const levelUsers: VideoRingUser[] = await Promise.all(
          levelUserIds.map(async (userId: string) => {
            const { name, initials } = await resolveUserInfo(userId, usersMap);
            
            // Determine status
            let status: VideoRingUser['status'] = 'missed';
            let rejectedAt: string | undefined;

            if ((ringRequest as any).accepted_by === userId) {
              status = 'accepted';
            } else {
              const rejection = rejections.find((r: any) => r.user_id === userId);
              if (rejection) {
                status = 'rejected';
                rejectedAt = rejection.rejected_at;
              } else if ((ringRequest as any).status === 'ringing' && (ringRequest as any).current_level >= level) {
                status = 'ringing';
              }
            }

            return { id: userId, name, initials, status, rejectedAt };
          })
        );

        levels.push({ level, users: levelUsers });
      }

      // Build requester info
      let requestedBy: VideoRingDetails['requestedBy'] = null;
      if ((ringRequest as any).requested_by) {
        const { name, initials } = await resolveUserInfo((ringRequest as any).requested_by, usersMap);
        requestedBy = { id: (ringRequest as any).requested_by, name, initials };
      }

      // Build accepted by info
      let acceptedBy: VideoRingDetails['acceptedBy'] = null;
      if ((ringRequest as any).accepted_by) {
        const { name, initials } = await resolveUserInfo((ringRequest as any).accepted_by, usersMap);
        acceptedBy = { id: (ringRequest as any).accepted_by, name, initials, status: 'accepted' };
      }

      return {
        id: (ringRequest as any).id,
        callId: (ringRequest as any).call_id,
        requestedBy,
        currentLevel: (ringRequest as any).current_level,
        status: (ringRequest as any).status,
        levels,
        acceptedBy,
        createdAt: (ringRequest as any).created_at,
        endedAt: (ringRequest as any).ended_at,
      };
    },
    enabled: !!callId,
    staleTime: 30000,
  });
}
